import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  BookOpen,
  FileText,
  HelpCircle,
  Video,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  ExternalLink,
} from "lucide-react";

const Resources = () => {
  const guides = [
    {
      icon: FileText,
      title: "How PR Distribution Works",
      description: "Learn the basics of press release distribution and how to maximize your reach.",
      link: "#",
    },
    {
      icon: Lightbulb,
      title: "Best Practices for Headlines",
      description: "Craft compelling headlines that grab attention and drive engagement.",
      link: "#",
    },
    {
      icon: CheckCircle,
      title: "Editorial Guidelines",
      description: "Understand our content standards and ensure your PR gets approved.",
      link: "#",
    },
    {
      icon: AlertCircle,
      title: "Content Acceptance Guidelines",
      description: "Know what content is accepted and what to avoid in your press releases.",
      link: "#",
    },
    {
      icon: Video,
      title: "Media Outreach Tips",
      description: "Strategies to get your press release picked up by journalists.",
      link: "#",
    },
    {
      icon: BookOpen,
      title: "White Label Case Study",
      description: "See how agencies use our white-label solution for their clients.",
      link: "#",
    },
  ];

  const faqs = [
    {
      question: "How long does it take for my press release to be published?",
      answer: "Most press releases are reviewed within 24-48 hours. Once approved, distribution begins immediately based on your selected channels.",
    },
    {
      question: "Can I edit my press release after submission?",
      answer: "You can edit draft press releases at any time. Once submitted for review, you'll need to wait for admin feedback before making changes.",
    },
    {
      question: "What file formats are accepted for supporting documents?",
      answer: "We accept PDF, JPG, PNG, DOC, and DOCX files up to 10MB per document.",
    },
    {
      question: "How do I track my press release performance?",
      answer: "Visit the Manage PR page to view detailed reports including distribution status, reach, and engagement metrics.",
    },
  ];

  return (
    <div className="space-y-8 max-w-5xl mx-auto animate-fade-in">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">
          Resources
        </h1>
        <p className="text-slate-600 mt-1">
          Guides, documentation, and best practices to help you succeed
        </p>
      </div>

      {/* Quick Start */}
      <Card className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-100">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="bg-blue-600 p-3 rounded-xl">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-heading font-semibold text-slate-900">
                New to PR Link Boost?
              </h2>
              <p className="text-slate-600 text-sm">
                Start with our quick start guide to learn the basics of press release distribution.
              </p>
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700">
              Get Started
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Guides Grid */}
      <div>
        <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">
          Documentation & Guides
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {guides.map((guide, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow cursor-pointer group">
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-3">
                  <div className="bg-slate-100 p-2 rounded-lg group-hover:bg-blue-100 transition-colors">
                    <guide.icon className="h-5 w-5 text-slate-600 group-hover:text-blue-600 transition-colors" />
                  </div>
                  {guide.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">{guide.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* FAQs */}
      <div>
        <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">
          Frequently Asked Questions
        </h2>
        <Card>
          <CardContent className="p-0 divide-y divide-slate-100">
            {faqs.map((faq, index) => (
              <div key={index} className="p-6">
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-slate-900 mb-2">{faq.question}</h3>
                    <p className="text-sm text-slate-600">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Contact Support */}
      <Card>
        <CardContent className="p-8 text-center">
          <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">
            Still have questions?
          </h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            Our support team is here to help. Get in touch and we'll respond within 24 hours.
          </p>
          <Button className="bg-blue-600 hover:bg-blue-700">
            Contact Support
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Resources;
